/**
 * Onboarding review sheet helpers: where extras are left, the guest-facing sentence, and restock levels.
 * Shared by the camera sheet and the import step so the preview and the saved text never differ.
 */
import { guestSentence } from "@/lib/guestGuideCopy";

export type ReviewKind = "unit" | "inventory" | "appliance" | "part";

export type ExtrasMode = "none" | "fixed" | "per_guests";

export type ExtrasRow = { id: string; guests: number; qty: number };

export type ReviewFields = {
  name: string;
  kind: ReviewKind;
  /** Extras for guests: none (skip), the same every stay, or more as the party grows. */
  extrasMode: ExtrasMode;
  /** Every stay: leave this many. */
  extrasQty: number;
  /** By guests: "If N guests, leave Q" rows. */
  extrasRows: ExtrasRow[];
  /** Where extras go: "spot" (where it lives), other spot ids, and/or "custom". Several allowed. */
  extrasPlaces: string[];
  extrasCustom: string;
  guestOn: boolean;
  /** Appliance only: owner-edited place, null = derived from the spot or room. */
  applPlace: string | null;
};

export const BUY_AT_OPTIONS: { label: string; value: number }[] = [
  { label: "3/4", value: 0.75 },
  { label: "1/2", value: 0.5 },
  { label: "1/4", value: 0.25 },
  { label: "Empty", value: 0 },
];

const PREPOSITION = /^(in|on|under|inside|at|by|next|behind|beside|above|below|near)\b/i;

/** "Cutlery drawer" → "in the cutlery drawer"; "Under the sink" → "under the sink". */
export function placeFromName(name: string): string {
  const n = name.trim();
  if (!n) return "";
  const lower = n.charAt(0).toLowerCase() + n.slice(1);
  if (PREPOSITION.test(lower)) return lower;
  return /^the\b/i.test(lower) ? `in ${lower}` : `in the ${lower}`;
}

/** The bare labels cleaners read for where extras go ("Under the sink", "Hall closet"), one per chosen place. */
export function extrasPlaceLabels(
  t: Pick<ReviewFields, "extrasPlaces" | "extrasCustom">,
  spotName: string | null,
  otherSpots: { id: string; name: string }[],
  room: string,
): string[] {
  const picked = t.extrasPlaces.length ? t.extrasPlaces : ["spot"];
  const out: string[] = [];
  for (const key of picked) {
    const label =
      key === "custom"
        ? t.extrasCustom.trim()
        : key === "spot"
          ? (spotName || room).trim()
          : otherSpots.find((s) => s.id === key)?.name.trim() || "";
    if (label && !out.includes(label)) out.push(label);
  }
  return out;
}

/** "in the pantry shelf and under the sink". */
export function joinPlaces(labels: string[]): string {
  const phrases = labels.map(placeFromName).filter(Boolean);
  if (phrases.length <= 1) return phrases[0] ?? "";
  return `${phrases.slice(0, -1).join(", ")} and ${phrases[phrases.length - 1]}`;
}

/** The rows the leave-out plan is built from. Every stay = one row with no guest count. */
export function extrasPlanRows(t: Pick<ReviewFields, "extrasMode" | "extrasQty" | "extrasRows">): { guests: number | null; qty: number }[] {
  if (t.extrasMode === "fixed") return t.extrasQty > 0 ? [{ guests: null, qty: t.extrasQty }] : [];
  if (t.extrasMode === "per_guests") {
    return [...t.extrasRows]
      .filter((r) => r.qty > 0)
      .sort((a, b) => a.guests - b.guests)
      .map((r) => ({ guests: r.guests, qty: r.qty }));
  }
  return [];
}

/** Where extras go, with the spot each place is (so the cleaner can see that spot's photo). */
export function extrasPlaceEntries(
  t: Pick<ReviewFields, "extrasPlaces" | "extrasCustom">,
  spotName: string | null,
  spotId: string | null,
  otherSpots: { id: string; name: string }[],
  room: string,
): { label: string; spotId: string | null }[] {
  const picked = t.extrasPlaces.length ? t.extrasPlaces : ["spot"];
  const out: { label: string; spotId: string | null }[] = [];
  for (const key of picked) {
    const entry =
      key === "custom"
        ? { label: t.extrasCustom.trim(), spotId: null }
        : key === "spot"
          ? { label: (spotName || room).trim(), spotId: spotName ? spotId : null }
          : { label: otherSpots.find((s) => s.id === key)?.name.trim() || "", spotId: key };
    if (entry.label && !out.some((o) => o.label === entry.label)) out.push(entry);
  }
  return out;
}

/** Leave-out plan for the cleaner's guest setup: each row splits its amount evenly across the chosen places. */
export function buildLeavePlan(
  t: ReviewFields,
  entries: { label: string; groupId: string | null }[],
): {
  rules: { id: string; guests: number | null; spots: { id: string; qty: number; place: string; group_id: string | null }[] }[];
} | null {
  const rows = extrasPlanRows(t);
  if (rows.length === 0 || entries.length === 0) return null;
  const id = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
  return {
    rules: rows.map((r) => {
      const base = Math.floor(r.qty / entries.length);
      const extra = r.qty - base * entries.length;
      return {
        id: id(),
        guests: r.guests,
        spots: entries
          .map((e, i) => ({ id: id(), qty: base + (i < extra ? 1 : 0), place: placeFromName(e.label), group_id: e.groupId }))
          .filter((sp) => sp.qty > 0),
      };
    }),
  };
}

/** The number the Inventory tab shows as the set quantity (the largest amount left out). */
export function extrasHeadlineQty(t: Pick<ReviewFields, "extrasMode" | "extrasQty" | "extrasRows">): number {
  const rows = extrasPlanRows(t);
  return rows.length ? Math.max(...rows.map((r) => r.qty)) : 0;
}

/** The sentence guests will see, or null when nothing should be shared. */
export function guestNoteText(
  t: ReviewFields,
  spotName: string | null,
  otherSpots: { id: string; name: string }[],
  room: string,
): string | null {
  if (!t.guestOn || !t.name.trim()) return null;
  if (t.kind === "inventory") {
    if (extrasPlanRows(t).length === 0) return null;
    const place = joinPlaces(extrasPlaceLabels(t, spotName, otherSpots, room));
    return guestSentence({ kind: "inventory", name: t.name, qty: null, place });
  }
  if (t.kind === "appliance") {
    const place = t.applPlace?.trim() || placeFromName(spotName || room);
    return guestSentence({ kind: "appliance", name: t.name, qty: null, place });
  }
  return null;
}
